/**
 * THE INTEGRALDELTA PRODUCT — the quantitative timing engines, and nothing astrological.
 *
 * <p>The exact mirror of {@code astro.manifest.js}: that product carries the ephemeris studies
 * and no scans, this one carries the scans and no ephemeris. Neither is a subset of the other,
 * which is why a single deny list could never have expressed the pair.
 *
 * <h2>⚠ WHAT THIS PRODUCT DOES NOT CARRY</h2>
 *
 * Every planetary study — the longitude and declination dialogs, the planetary and
 * average-longitude lines, the planet-follow and characteristics plots, the degree-range and
 * area highlights, the Visual Universe. Those are the OTHER product. A reader here sees CTS
 * orbs and band cycles, which are derived from ephemeris dates and expose none of the astrology.
 */

import { CORE_DRAWING, CORE_INDICATORS, CLEAR_ACTIONS } from './astro.manifest';

/*
 * Shared with astro and stated there: the brief names tools, not the chart's own view and export
 * controls, and a product that cannot switch to candles or save a picture is not what was asked
 * for. Declared separately in each manifest so one can drop it without the other.
 */
/*
 * ⚠ `watermark_toggle` IS NOT HERE, and it was until a test caught it.
 *
 * The control is `props.isAdmin &&` gated in the toolbar, so a customer manifest naming it
 * claimed a capability the role gate withholds — a manifest that is not the product definition,
 * which is the one thing this architecture may not be. It lives in the admin manifest alone.
 */
const VIEW_CHROME = Object.freeze([
  'chart_type',
  'area_zoom',
  'save_screenshot',
  'copy_chart',
  'configuration',
  /*
   * ⚠ `messages_panel` ON THE BAR, integralDelta ONLY — and it is a SECOND route, not the first.
   *
   * The header already carries `MessagesPanelButton` on all four products, and it is present and
   * visible here: measured 30x30 at (3147,17) with the brand reading `integralDelta.ai`. So this
   * does not fix an absence in the chrome — it answers the request for the toggle to sit *in the
   * toolbar strip, beside the search and the two clears*, which is a different region entirely
   * (the header has no search and no clear).
   *
   * <p>Why only here: this is the research product. Every study that writes to the Summary and
   * Journal tabs — the CTS scans, the PTA/PTB cycle runs, the three engine runners — is run from
   * this bar, so the control belongs in the reader's hand where the work happens. astro and
   * dhanabhyas keep the header route alone; adding it there is one string in each of their own
   * `VIEW_CHROME` copies, which is why these lists are per-product rather than shared.
   *
   * <p>⚠ IT RENDERS FROM HAND-WRITTEN JSX, not the `ICONS` loop — `messages_panel` has no `ICONS`
   * entry, so naming it in a manifest alone would have done NOTHING: the manifest is a membership
   * filter over that array, and there was no member to admit. It is gated by `showsTool`, like
   * `area_zoom` and `clear_all` either side of it.
   */
  'messages_panel',
]);

export const integraldeltaManifest = Object.freeze({
  id: 'integraldelta',

  brandName: 'integralDelta.ai',
  watermark: 'integralDelta.ai',

  domains: Object.freeze([
    'integraldelta.ai',
    'www.integraldelta.ai',
    'integraldelta.com',
    'www.integraldelta.com',
    'integraldelta.web.app',
  ]),

  toolbar: Object.freeze([
    /*
     * The three CTS orbs, tightest first. `cts_scan_1` is orb 1.0 and the only one whose
     * published figures clear their costs; 2 and 3 are the looser orbs and are on the bar here
     * because this is the product they belong to.
     */
    'cts_scan_1',
    'cts_scan_2',
    'cts_scan_3',

    /* The two band cycles. PTA is the ~225-day cycle, PTB the ~88-day one. */
    'pta_cycle',
    'ptb_cycle',

    /*
     * THE MARKET SCANNER — added 2026-11-02 to close a gap where the UI was STRICTER than the
     * server.
     *
     * <h2>⚠ THIS ENTRY AND `ProductAudience.allowsIntegralDeltaFeature()` ARE ONE RULE</h2>
     *
     * The backend permits an ADMIN **or** an integralDelta customer and answers an astro customer
     * with 403. Stage 1 left no customer manifest naming the tool, so an integralDelta customer
     * the server would have served could not reach the icon — a capability withheld from somebody
     * entitled to it.
     *
     * <p>It is on THIS product alone, which is the whole point: astro and dhanabhyas must not
     * name it, because there the server refuses and a visible button would 403 on every click.
     * That is the mirror defect, and it is the worse one — the cycle pills shipped it once, made
     * customer-facing while the server still refused non-admins heliocentric frames.
     *
     * <p>⚠ THE ROLE GATE MUST AGREE TOO, and it already does: the icon carries `adminOnly: true`
     * (which is what withholds it on astro) AND sits in
     * `CUSTOMER_FACING_BY_PRODUCT[integraldelta]` (which is what reveals it here). Three places,
     * one rule; `productVisibility.test.js` composes all of them rather than asserting any alone.
     */
    'market_scanner',

    ...CORE_DRAWING,

    /*
     * ⚠ RESTORED 2026-11-01, having left the bar in Stage 1 as a consequence of the brief's
     * explicit tool lists. It is standard chart functionality — zoom in, click the two bars that
     * define a cycle, and the dialog draws it — not a research instrument, so withholding it from
     * a customer was the allow-list being read more literally than it was meant.
     */
    'cycles_dialog',
    ...CORE_INDICATORS,
    ...VIEW_CHROME,
    ...CLEAR_ACTIONS,
  ]),

  /** No Admin Panel — see the note in `astro.manifest.js`. */
  adminPanel: null,
});

export default integraldeltaManifest;
